/**
 * useBoxOfficeProjections Hook
 * Manages box office projection data fetching, state management, and operations
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import apiService from '../lib/services/apiService';
import { 
  validateCreateProjectionRequest,
  validateUpdateProjectionRequest,
  validateFilmId
} from '../types/boxOfficeTypes';

/**
 * Custom hook for managing box office projections
 * @param {string} filmId - IMDB ID of the film (optional, can be set per operation)
 * @returns {Object} - Hook state and methods
 */
export const useBoxOfficeProjections = (filmId = null) => {
  // State management
  const [projections, setProjections] = useState({
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
    pagination: null
  });

  const [projectionOperations, setProjectionOperations] = useState({
    loading: false,
    error: null,
    lastOperation: null,
    lastOperationTime: null
  });

  // Refs for managing async operations
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Set projections state
   * @param {Object} updates - State updates
   */
  const setProjectionsUpdate = useCallback((updates) => {
    if (mountedRef.current) {
      setProjections(prev => ({ ...prev, ...updates }));
    }
  }, []);

  /**
   * Set projection operations state
   * @param {Object} updates - State updates
   */
  const setProjectionOperationsUpdate = useCallback((updates) => {
    if (mountedRef.current) {
      setProjectionOperations(prev => ({ ...prev, ...updates }));
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    if (mountedRef.current) {
      setProjectionsUpdate({ error: null });
      setProjectionOperationsUpdate({ error: null });
    }
  }, [setProjectionsUpdate, setProjectionOperationsUpdate]);

  /**
   * Fetch box office projections for a specific film
   * @param {string} targetFilmId - IMDB ID of the film
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - Array of projections
   */
  const fetchFilmProjections = useCallback(async (targetFilmId = filmId, params = {}) => {
    // Validate film ID
    const filmIdValidation = validateFilmId(targetFilmId);
    if (!filmIdValidation.isValid) {
      setProjectionsUpdate({
        loading: false,
        error: filmIdValidation.error
      });
      return [];
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Set loading state
    setProjectionsUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await apiService.getFilmBoxOfficeProjections(targetFilmId, params);

      if (!mountedRef.current) return [];

      if (response.success && response.data) {
        const projectionsData = response.data.projections || [];
        setProjectionsUpdate({
          data: projectionsData,
          loading: false,
          error: null,
          lastFetched: new Date().toISOString(),
          pagination: response.pagination
        });
        return projectionsData;
      } else {
        setProjectionsUpdate({
          loading: false,
          error: 'Failed to fetch box office projections'
        });
        return [];
      }
    } catch (error) {
      if (!mountedRef.current) return [];

      // Don't set error if request was aborted
      if (error.name === 'AbortError') {
        return [];
      }

      // Log API errors for debugging
      console.error('❌ Box Office Projections API Error:', error.message);

      const errorMessage = error.message || 'Failed to fetch box office projections';
      setProjectionsUpdate({
        loading: false,
        error: errorMessage
      });
      return [];
    } finally {
      abortControllerRef.current = null;
    }
  }, [filmId, setProjectionsUpdate]);

  /**
   * Fetch all box office projections with filtering
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - Array of projections
   */
  const fetchAllProjections = useCallback(async (params = {}) => {
    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Set loading state
    setProjectionsUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await apiService.getBoxOfficeProjections(params);

      if (!mountedRef.current) return [];

      if (response.success && response.data) {
        const projectionsData = response.data || [];
        setProjectionsUpdate({
          data: projectionsData,
          loading: false,
          error: null,
          lastFetched: new Date().toISOString(),
          pagination: response.pagination
        });
        return projectionsData;
      } else {
        setProjectionsUpdate({
          loading: false,
          error: 'Failed to fetch box office projections'
        });
        return [];
      }
    } catch (error) {
      if (!mountedRef.current) return [];

      // Don't set error if request was aborted
      if (error.name === 'AbortError') {
        return [];
      }

      // Log API errors for debugging
      console.error('❌ Box Office Projections API Error:', error.message);

      const errorMessage = error.message || 'Failed to fetch box office projections';
      setProjectionsUpdate({
        loading: false,
        error: errorMessage
      });
      return [];
    } finally {
      abortControllerRef.current = null;
    }
  }, [setProjectionsUpdate]);

  /**
   * Create a new box office projection
   * @param {Object} projectionData - Projection data
   * @returns {Promise<Object>} - Created projection
   */
  const createProjection = useCallback(async (projectionData) => {
    // Validate projection data
    const validation = validateCreateProjectionRequest(projectionData);
    if (!validation.isValid) {
      setProjectionOperationsUpdate({
        loading: false,
        error: validation.error
      });
      throw new Error(validation.error);
    }

    // Set loading state
    setProjectionOperationsUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await apiService.createBoxOfficeProjection(projectionData);

      if (!mountedRef.current) return null;

      if (response.success) {
        setProjectionOperationsUpdate({
          loading: false,
          error: null,
          lastOperation: 'create',
          lastOperationTime: new Date().toISOString()
        });

        // Refresh projections if this is for the current film
        if (projectionData.filmId === filmId) {
          await fetchFilmProjections(filmId);
        }

        return response;
      } else {
        throw new Error('Failed to create box office projection');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      // Log API errors for debugging
      console.error('❌ Create Box Office Projection API Error:', error.message);

      const errorMessage = error.message || 'Failed to create box office projection';
      setProjectionOperationsUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [filmId, fetchFilmProjections, setProjectionOperationsUpdate]);

  /**
   * Update an existing box office projection
   * @param {string} projectionId - ID of the projection to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Updated projection
   */
  const updateProjection = useCallback(async (projectionId, updates) => {
    // Validate update data
    const validation = validateUpdateProjectionRequest(updates);
    if (!validation.isValid) {
      setProjectionOperationsUpdate({
        loading: false,
        error: validation.error
      });
      throw new Error(validation.error);
    }

    // Set loading state
    setProjectionOperationsUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await apiService.updateBoxOfficeProjection(projectionId, updates);

      if (!mountedRef.current) return null;

      if (response.success) {
        setProjectionOperationsUpdate({
          loading: false,
          error: null,
          lastOperation: 'update',
          lastOperationTime: new Date().toISOString()
        });

        // Refresh projections
        if (filmId) {
          await fetchFilmProjections(filmId);
        }

        return response;
      } else {
        throw new Error('Failed to update box office projection');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      // Log API errors for debugging
      console.error('❌ Update Box Office Projection API Error:', error.message);

      const errorMessage = error.message || 'Failed to update box office projection';
      setProjectionOperationsUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [filmId, fetchFilmProjections, setProjectionOperationsUpdate]);

  /**
   * Delete a box office projection
   * @param {string} projectionId - ID of the projection to delete
   * @returns {Promise<Object>} - Delete result
   */
  const deleteProjection = useCallback(async (projectionId) => {
    if (!projectionId) {
      setProjectionOperationsUpdate({
        loading: false,
        error: 'Projection ID is required'
      });
      throw new Error('Projection ID is required');
    }

    // Set loading state
    setProjectionOperationsUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await apiService.deleteBoxOfficeProjection(projectionId);

      if (!mountedRef.current) return null;

      if (response.success) {
        setProjectionOperationsUpdate({
          loading: false,
          error: null,
          lastOperation: 'delete',
          lastOperationTime: new Date().toISOString()
        });

        // Refresh projections
        if (filmId) {
          await fetchFilmProjections(filmId);
        }

        return response;
      } else {
        throw new Error('Failed to delete box office projection');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      // Log API errors for debugging
      console.error('❌ Delete Box Office Projection API Error:', error.message);

      const errorMessage = error.message || 'Failed to delete box office projection';
      setProjectionOperationsUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [filmId, fetchFilmProjections, setProjectionOperationsUpdate]);

  /**
   * Refresh projections data
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - Refreshed projections
   */
  const refreshProjections = useCallback(async (params = {}) => {
    if (filmId) {
      return await fetchFilmProjections(filmId, params);
    } else {
      return await fetchAllProjections(params);
    }
  }, [filmId, fetchFilmProjections, fetchAllProjections]);

  /**
   * Get projections sorted by date
   * @param {string} order - Sort order ('asc' or 'desc')
   * @returns {Array} - Sorted array of projections
   */
  const getProjectionsSortedByDate = useCallback((order = 'desc') => {
    const projectionsData = projections.data || [];
    return [...projectionsData].sort((a, b) => {
      const dateA = new Date(a.projectionDate);
      const dateB = new Date(b.projectionDate);
      return order === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [projections.data]);

  /**
   * Get projections sorted by actual projection amount
   * @param {string} order - Sort order ('asc' or 'desc')
   * @returns {Array} - Sorted array of projections
   */
  const getProjectionsSortedByAmount = useCallback((order = 'desc') => {
    const projectionsData = projections.data || [];
    return [...projectionsData].sort((a, b) => {
      const amountA = a.actualProjection || 0;
      const amountB = b.actualProjection || 0;
      return order === 'desc' ? amountB - amountA : amountA - amountB;
    });
  }, [projections.data]);

  /**
   * Get latest projection
   * @returns {Object|null} - Latest projection or null
   */
  const getLatestProjection = useCallback(() => {
    const sortedProjections = getProjectionsSortedByDate('desc');
    return sortedProjections.length > 0 ? sortedProjections[0] : null;
  }, [getProjectionsSortedByDate]);

  /**
   * Get projection trend (up, down, stable)
   * @returns {string} - Trend direction
   */
  const getProjectionTrend = useCallback(() => {
    const sortedProjections = getProjectionsSortedByDate('desc');
    if (sortedProjections.length < 2) return 'stable';

    const latest = sortedProjections[0];
    const previous = sortedProjections[1];

    const change = latest.actualProjection - previous.actualProjection;
    const changePercentage = (change / previous.actualProjection) * 100;

    if (changePercentage > 5) return 'up';
    if (changePercentage < -5) return 'down';
    return 'stable';
  }, [getProjectionsSortedByDate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Return hook interface
  return {
    // Projections state
    projections: projections.data,
    loading: projections.loading,
    error: projections.error,
    lastFetched: projections.lastFetched,
    pagination: projections.pagination,
    
    // Operations state
    operationLoading: projectionOperations.loading,
    operationError: projectionOperations.error,
    lastOperation: projectionOperations.lastOperation,
    lastOperationTime: projectionOperations.lastOperationTime,
    
    // Methods
    fetchFilmProjections,
    fetchAllProjections,
    createProjection,
    updateProjection,
    deleteProjection,
    refreshProjections,
    clearError,
    
    // Utility methods
    getProjectionsSortedByDate,
    getProjectionsSortedByAmount,
    getLatestProjection,
    getProjectionTrend,
    
    // State helpers
    isLoaded: !projections.loading && !projections.error,
    hasError: !!projections.error,
    hasProjections: !projections.loading && !projections.error && projections.data.length > 0,
    isEmpty: !projections.loading && !projections.error && projections.data.length === 0
  };
};

export default useBoxOfficeProjections;
